import {
  isAreaFeature, type Coordinate, type EditableFloorPlan, type EditorFeature,
} from '../models/editor-feature';
import { toScreen, type Projection } from './projection';
import { isUsableScale, type Scale } from './measurement';
import { editableFeaturesOf } from './selection';

/**
 * Whether two points are close enough to be treated as the same one.
 *
 * Answers a question the polygon tool has to ask anyway: has the shape being drawn come back
 * to its own first vertex? Measured on screen rather than in the plan, because the tolerance
 * has to mean the same thing to the hand at every zoom — a plan-unit tolerance would be
 * unreachable on a large site and would swallow whole rooms on a small one.
 */

/** How close, in screen pixels, two points have to be to count as one. */
export const SAME_POINT_TOLERANCE = 10;

export const isSamePoint = (
  first: Coordinate,
  second: Coordinate,
  projection: Projection,
  tolerance = SAME_POINT_TOLERANCE,
): boolean => {
  const a = toScreen(first, projection);
  const b = toScreen(second, projection);

  return Math.hypot(a.x - b.x, a.y - b.y) <= tolerance;
};

/**
 * Snapping, as an option the admin switches on.
 *
 * It was once always on, with four constraints stacked, and a pointer that quietly refused to
 * go where it was put read as a broken mouse. It is back as two plain switches — to other
 * shapes, and to a grid — off unless chosen, with a marker showing where the point was pulled
 * to, so a snapped click is never a surprise.
 */
export interface SnapSettings {
  /** Pull to the corners of other shapes and to desks and labels, when the pointer is near one. */
  readonly toShapes: boolean;
  /** Round to the snapping grid. */
  readonly toGrid: boolean;
}

export const DEFAULT_SNAP_SETTINGS: SnapSettings = { toShapes: false, toGrid: false };

export type SnapKind = 'point' | 'grid' | 'none';

export interface SnapResult {
  readonly at: Coordinate;
  readonly kind: SnapKind;
}

/** Half a metre: the unit desks, walls and rooms are laid out to in an office. */
export const GRID_SPACING_METRES = 0.5;

/** The snapping grid on a plan with no scale: a hundredth of its width, rounded to 1, 2 or 5. */
const niceSpacing = (roughly: number): number => {
  if (!(roughly > 0) || !Number.isFinite(roughly)) {
    return 1;
  }

  const magnitude = 10 ** Math.floor(Math.log10(roughly));
  const step = [1, 2, 5, 10].find((candidate) => candidate * magnitude >= roughly) ?? 10;

  return step * magnitude;
};

/** How far apart snapping grid points are, in plan units. */
export const gridSpacingOf = (scale: Scale, planWidth: number): number => (
  isUsableScale(scale) ? GRID_SPACING_METRES * scale : niceSpacing(planWidth / 100)
);

/**
 * The points a shape snap may pull to: every corner of every area, and every desk and label.
 *
 * `exclude` is what is being moved or reshaped. A shape that snapped to its own corners would
 * stick where it started, which is exactly the broken-mouse feeling this has to avoid.
 */
export const snapTargetsOf = (
  floorPlan: EditableFloorPlan,
  exclude: ReadonlySet<string> = new Set(),
  hidden: ReadonlySet<string> = new Set(),
): Coordinate[] => editableFeaturesOf(floorPlan)
  .filter((feature: EditorFeature) => !exclude.has(feature.id) && !hidden.has(feature.id))
  .flatMap((feature): Coordinate[] => (isAreaFeature(feature)
    ? feature.coordinates.flatMap((polygon) => polygon.flat())
    : [feature.coordinates]));

/**
 * Where a point lands once snapping has had its say.
 *
 * A nearby shape point wins over the grid: lining a new room up against an existing wall is the
 * reason to snap at all, and a grid point a few centimetres off it would defeat that.
 *
 * @param tolerance How close, in plan units, a shape point has to be to pull. Derived from
 * screen pixels by the caller, so it means the same distance to the hand at every zoom.
 */
export const snapPoint = (
  at: Coordinate,
  settings: SnapSettings,
  options: {
    readonly targets: readonly Coordinate[];
    readonly tolerance: number;
    readonly gridSpacing: number;
  },
): SnapResult => {
  if (settings.toShapes && options.tolerance > 0) {
    let best: Coordinate | undefined;
    let bestDistance = options.tolerance;

    options.targets.forEach((target) => {
      const distance = Math.hypot(target[0] - at[0], target[1] - at[1]);

      if (distance <= bestDistance) {
        bestDistance = distance;
        best = target;
      }
    });

    if (best) {
      return { at: [best[0], best[1]], kind: 'point' };
    }
  }

  if (settings.toGrid && options.gridSpacing > 0) {
    const step = options.gridSpacing;

    return { at: [Math.round(at[0] / step) * step, Math.round(at[1] / step) * step], kind: 'grid' };
  }

  return { at, kind: 'none' };
};
